import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { Coins, Loader2 } from 'lucide-react';

/**
 * CreditBalance component displays the user's confirmed credit balance.
 * 
 * Rules:
 * - Only shows when balance is confirmed from backend (not null)
 * - Shows loading state when refreshing
 * - Hides completely if balance is unavailable (never guesses)
 */
const CreditBalance = ({ 
  className = "", 
  showLabel = true,
  size = "default", // "default" | "sm" | "lg"
  onClick = null
}) => {
  const { creditBalance, balanceLoading, hasConfirmedBalance, isAuthenticated } = useAuth();

  // Don't show if not authenticated
  if (!isAuthenticated) return null;

  // Don't show if balance is unavailable (null) - hide rather than guess
  if (!hasConfirmedBalance && !balanceLoading) return null;

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    default: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5"
  };

  const iconSizes = {
    sm: "h-3 w-3",
    default: "h-4 w-4",
    lg: "h-5 w-5"
  };

  const Wrapper = onClick ? 'button' : 'div';
  const wrapperProps = onClick ? { onClick, type: 'button' } : {};

  return (
    <Wrapper {...wrapperProps}>
      <Badge 
        variant="outline" 
        className={`${sizeClasses[size]} font-medium bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 ${onClick ? 'cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors' : ''} ${className}`}
        data-testid="credit-balance-badge"
      >
        {balanceLoading ? (
          <Loader2 className={`${iconSizes[size]} mr-1 animate-spin`} />
        ) : (
          <Coins className={`${iconSizes[size]} mr-1`} />
        )}
        {balanceLoading ? (
          <span className="opacity-50">...</span>
        ) : (
          <>
            {creditBalance}
            {showLabel && <span className="ml-1">credit{creditBalance !== 1 ? 's' : ''}</span>}
          </>
        )}
      </Badge>
    </Wrapper>
  );
};

export default CreditBalance;
